import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Tabs,
  Tab,
  TablePagination,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search,
  Edit,
  CallMerge,
  Delete,
  LocalOffer,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import useShipments from "../../hooks/useShipments";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import { selectCurrentUser, selectActiveTin } from "../../redux/slices/authSlice";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import AnalyzeWithAiButton from "../../components/common/AnalyzeWithAiButton";
import NewShipmentSplitButton from "../../components/common/NewShipmentSplitButton";
import ConsolidationPickerDialog from "../../components/common/ConsolidationPickerDialog";
import ConsolidationDetailDialog from "../../components/common/ConsolidationDetailDialog";
import { selectKeyForRole, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";
import { formatUsd } from "../../utils/currency";
import { slugifyTab } from "../../utils/tabSlug";

const TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Awaiting Action", value: "awaiting_invoice" },
  { label: "Delivered", value: "delivered" },
];

const truncateWords = (text, maxWords = 3) => {
  if (!text) return "—";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const URL_SCHEMA = {
  // The tab's slugified label ("all", "awaiting_action", …) rather than
  // its array index — stable if tabs are ever reordered, and readable in
  // the address bar/shared links instead of a meaningless number.
  tab: { default: "" },
  search: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const CustomerShipments = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useSelector(selectCurrentUser);
  const activeTin = useSelector(selectActiveTin);
  const { shipments, pagination, loading, load, remove } =
    useShipments();
  const itemReceivedKey = useSelector(selectKeyForRole("item", "received"));
  const itemStatusOrder = useSelector(selectStatusOrder("item"));
  const receivedIdx = itemStatusOrder.indexOf(itemReceivedKey);
  const hasConsolidationEligibleItem = (s) =>
    s.items?.some((item) => {
      if (item.consolidation) return false;
      const idx = item.itemStatus ? itemStatusOrder.indexOf(item.itemStatus) : 0;
      return receivedIdx === -1 || idx <= receivedIdx;
    });
  // All items on one shipment that join a consolidation always land in the
  // same Consolidation record (see server/models/Consolidation.js), so the
  // first populated item.consolidation found is enough to represent it.
  const getShipmentConsolidation = (s) =>
    s.items?.find((item) => item.consolidation && item.consolidation.consolidationNumber)
      ?.consolidation;

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { tab, page, rowsPerPage } = urlState;
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  // Local typing buffer — committed to the URL (and re-queried) only on
  // Enter, same as before.
  const [search, setSearch] = useState(urlState.search);
  const [consolidationPicker, setConsolidationPicker] = useState(null);
  const [viewConsolidationId, setViewConsolidationId] = useState(null);

  const doLoad = (overrides = {}) => {
    const tabVal = TABS[activeTab].value;
    load({
      status: tabVal || undefined,
      // "All" tab means "everything in progress" — delivered shipments
      // only show under their own Delivered tab.
      excludeDelivered: tabVal === "" ? true : undefined,
      search: search || undefined,
      page: page + 1,
      limit: rowsPerPage,
      tin: activeTin || undefined,
      ...overrides,
    });
  };

  useEffect(() => {
    doLoad();
  }, [activeTab, page, rowsPerPage, activeTin]);

  const hasTin = user?.canManageMultipleTins
    ? (user?.tins?.length || 0) > 0
    : !!user?.tin;

  const handleOpenCreate = () => {
    if (!hasTin) {
      enqueueSnackbar(
        "Please add your TIN to your profile before creating a shipment",
        { variant: "warning" },
      );
      navigate("/dashboard/profile");
      return;
    }
    navigate("/dashboard/shipment/add");
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setUrlState({ search: e.target.value, page: 0 });
      doLoad({ page: 1, search: e.target.value });
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete shipment ${s.trackingNumber}? This cannot be undone.`)) return;
    const ok = await remove(s._id);
    if (ok) doLoad();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="My Shipments"
        subtitle={`${pagination.total || shipments.length} total shipments`}
        action={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: 1.5,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <AnalyzeWithAiButton />
            <NewShipmentSplitButton
              onNewShipment={handleOpenCreate}
              disabled={!hasTin}
              disabledTooltip="Add your TIN to your profile before creating a shipment"
              fullWidth={isMobile}
              canBulkAdd={!!user?.canBulkAddShipments}
              onBulkAdd={() => navigate("/dashboard/shipments/bulk-add")}
              canBulkImport={!!user?.canBulkImportShipments}
              onBulkImport={() => navigate("/dashboard/shipments/bulk-import")}
            />
          </Box>
        }
      />

      {!hasTin && (
        <Alert
          severity="warning"
          variant="filled"
          sx={{ fontWeight: 700, fontSize: 15, alignItems: "center" }}
        >
          You need to add your TIN to your profile before you can create a new
          shipment.{" "}
          <Button
            size="small"
            variant="outlined"
            sx={{
              ml: 1,
              color: "#fff",
              borderColor: "rgba(255,255,255,0.7)",
              fontWeight: 700,
              "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" },
            }}
            onClick={() => navigate("/dashboard/profile")}
          >
            Add TIN
          </Button>
        </Alert>
      )}

      <Card>
        <Box
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setUrlState({ tab: slugifyTab(TABS[v].label), page: 0 })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              maxWidth: "100%",
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
          <Box sx={{ pb: 1, width: { xs: "100%", sm: "auto" } }}>
            <TextField
              size="small"
              placeholder="Search tracking, vendor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
              sx={{ width: { xs: "100%", sm: 220 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>

        {loading ? (
          <SkeletonLoader rows={Math.min(6, rowsPerPage)} />
        ) : shipments.length === 0 ? (
          <EmptyState
            icon="📦"
            title="No shipments yet"
            description="Create your first shipment to start tracking your imports."
            actionLabel="New Shipment"
            onAction={handleOpenCreate}
            actionDisabled={!hasTin}
            actionDisabledReason="Add your TIN to your profile before creating a shipment"
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tracking #</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Invoice Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => {
                    // Server-computed from each item's own status — see
                    // shipmentStatusService.computeCustomerEditable.
                    const isEditable = s.customerEditable !== false;
                    return (
                      <TableRow
                        key={s._id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/dashboard/shipments/${s.trackingNumber}`)
                        }
                      >
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s.trackingNumber}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Tooltip title={s.packageDescription || s.vendor?.name || ""}>
                              <Typography noWrap variant="body2" fontWeight={600}>
                                {truncateWords(s.packageDescription || s.vendor?.name)}
                              </Typography>
                            </Tooltip>
                            {(s.promoStatus === "applied" || s.promoStatus === "pending") && (
                              <Tooltip title={s.promoStatus === "applied" ? "Promo applied" : "Promo pending — confirmed once weighed in"}>
                                <LocalOffer
                                  sx={{
                                    fontSize: 15,
                                    color: s.promoStatus === "applied" ? "#00a6b2" : "#b8860b",
                                    flexShrink: 0,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          {(s.vendor?.name || s.orderNumber) && (
                            <Typography noWrap variant="caption" color="text.secondary">
                              {[s.vendor?.name, s.orderNumber && `Order #${s.orderNumber}`]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{s.items?.length || 0}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatUsd(s.invoiceTotal)}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={s.status} domain="shipment" />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {dayjs(s.createdAt).format("MMM D")}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            {(() => {
                              const consolidation = getShipmentConsolidation(s);
                              if (consolidation) {
                                return (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<CallMerge fontSize="small" />}
                                    onClick={() =>
                                      setViewConsolidationId(consolidation._id)
                                    }
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    Consolidated
                                  </Button>
                                );
                              }
                              return (
                                hasConsolidationEligibleItem(s) && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CallMerge fontSize="small" />}
                                    onClick={() =>
                                      setConsolidationPicker({ shipment: s })
                                    }
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    Consolidate
                                  </Button>
                                )
                              );
                            })()}
                            {isEditable && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Edit fontSize="small" />}
                                onClick={() => navigate(`/dashboard/shipment/${s.trackingNumber}/edit`)}
                              >
                                Edit
                              </Button>
                            )}
                            {isEditable && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<Delete fontSize="small" />}
                                onClick={() => handleDelete(s)}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={pagination.total || shipments.length}
              page={page}
              onPageChange={(_, p) => setUrlState({ page: p })}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>

      <ConsolidationPickerDialog
        open={!!consolidationPicker}
        onClose={() => setConsolidationPicker(null)}
        mode="shipment"
        shipmentId={consolidationPicker?.shipment?._id}
        customerId={
          consolidationPicker?.shipment?.customer?._id ||
          consolidationPicker?.shipment?.customer
        }
        onDone={() => doLoad()}
      />

      <ConsolidationDetailDialog
        open={!!viewConsolidationId}
        onClose={() => setViewConsolidationId(null)}
        consolidationId={viewConsolidationId}
        isAdmin={false}
        onChanged={() => doLoad()}
      />

    </Box>
  );
};

export default CustomerShipments;